import { useMemo, useState } from "react";
import type { ColumnFiltersState, PaginationState } from "@tanstack/react-table";
import { Plus } from "lucide-react";

import { pageCount } from "@/core/api/pagination";
import { FLASHPAY_ROLES, RequireRole } from "@/core/auth";
import { DataTableClar } from "@/core/components/shared/data-table";
import { Button } from "@/core/components/ui/button";
import { useProductProviderMappings } from "@/features/ledger/api/use-product-provider-mappings";
import type { ProductProviderMappingFilter } from "@/features/ledger/api/dto";
import {
  ProductProviderMappingFormDialog,
  productProviderMappingsColumns,
  type ProductProviderMappingRow,
} from "@/features/ledger/components";
import { useLedgerTranslation } from "@/features/ledger/i18n/use-ledger-translation";

/** Traduit les filtres de colonnes DataTableClar en `ProductProviderMappingFilter` (§4.2). */
const toMappingFilter = (
  filters: ColumnFiltersState
): ProductProviderMappingFilter => {
  const filter: ProductProviderMappingFilter = {};
  for (const { id, value } of filters) {
    if (typeof value !== "string" || !value) continue;
    if (id === "productCodeId") filter.productCodeId = value;
    if (id === "providerId") filter.providerId = value;
  }
  return filter;
};

/**
 * Associations code produit / provider paginées
 * (`POST /ProductProviderMappings/all`, guide §6.4) — lecture authentifiée ;
 * création réservée à flashpay-superadmin / flashpay-admin, désactivation à
 * flashpay-superadmin.
 */
export function ProductProviderMappingsPage() {
  const { t } = useLedgerTranslation();
  const [createOpen, setCreateOpen] = useState(false);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  });
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const params = useMemo(
    () => ({
      filter: toMappingFilter(columnFilters),
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
    }),
    [columnFilters, pagination]
  );

  const { data, isLoading, isFetching, error, refetch } =
    useProductProviderMappings(params);

  return (
    <section className="flex flex-col gap-4">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold text-foreground">
          {t("ledger.mappings.title")}
        </h1>
        <RequireRole
          roles={[FLASHPAY_ROLES.SUPERADMIN, FLASHPAY_ROLES.ADMIN]}
        >
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="mr-2 w-4 h-4" />
            {t("ledger.mappings.create")}
          </Button>
        </RequireRole>
      </div>

      <DataTableClar
        data={(data?.items ?? []).filter((r): r is ProductProviderMappingRow =>
          Boolean(r.id)
        )}
        columnsClar={productProviderMappingsColumns}
        title={t("ledger.mappings.title")}
        exportFileName="product-provider-mappings"
        paginationMode="server"
        serverPageCount={data ? pageCount(data) : 0}
        totalItemsCount={data?.total ?? 0}
        pagination={pagination}
        onPaginationChange={setPagination}
        columnFilters={columnFilters}
        onColumnFiltersChange={setColumnFilters}
        loading={{ isLoading, isFetching }}
        error={
          error
            ? { message: error.message, refetch: () => void refetch() }
            : undefined
        }
      />

      <ProductProviderMappingFormDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
      />
    </section>
  );
}
